import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getConversionReport } from "../../../actions/adminReportActions";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);
import { niceMax } from '../../../utils/chartPlugins';
import Loader from "../../Loader";
import { downloadCSV } from '../../../utils/exportCsv';

const ConversionReport = () => {
  const dispatch = useDispatch();
  const { conversionReport, loading } = useSelector(state => state.adminReport);

  const [dateRange, setDateRange] = useState('last30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const rangeLabel = (dr, s, e) => {
    if (dr === 'custom') return s && e ? `${s} → ${e}` : 'Custom range';
    if (dr === 'last7days') return 'Last 7 Days';
    if (dr === 'last30days') return 'Last 30 Days';
    if (dr === 'last90days') return 'Last 90 Days';
    if (dr === 'ytd') return 'Year to Date';
    return dr;
  };

  useEffect(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        dispatch(getConversionReport('custom', customStartDate, customEndDate));
      }
    } else {
      dispatch(getConversionReport(dateRange));
    }
  }, [dispatch, dateRange, customStartDate, customEndDate]);

  const signupsSeries = conversionReport?.signupsSeries || [];
  const paidSeries = conversionReport?.paidSeries || [];

  const labels = Array.from(new Set([...signupsSeries.map(s => s.date), ...paidSeries.map(p => p.date)])).sort();

  const signupsMap = Object.fromEntries(signupsSeries.map(s => [s.date, s.signups]));
  const paidMap = Object.fromEntries(paidSeries.map(p => [p.date, p.paid]));

  const chartData = {
    labels,
    datasets: [
      { label: 'Signups', data: labels.map(d => Number(signupsMap[d] || 0)), backgroundColor: '#00a65a' },
      { label: 'Paid', data: labels.map(d => Number(paidMap[d] || 0)), backgroundColor: '#3c8dbc' },
      // conversion percentage per day (line, right axis)
      { label: 'Conversion %', data: labels.map(d => {
          const s = Number(signupsMap[d] || 0);
          const p = Number(paidMap[d] || 0);
          return s > 0 ? Math.round((p / s) * 100 * 100) / 100 : 0;
        }), type: 'line', yAxisID: 'percent', borderColor: '#ff9900', backgroundColor: '#ff9900', tension: 0.2, pointRadius: 3, fill: false }
    ]
  };

  const combinedValues = [...chartData.datasets[0].data, ...chartData.datasets[1].data];
  const chartMax = niceMax(combinedValues);

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: 'top' }, tooltip: { mode: 'index', intersect: false,
      callbacks: {
        label: function(context) {
          if (context.dataset && context.dataset.label === 'Conversion %') {
            return `${context.dataset.label}: ${context.parsed.y}%`;
          }
          return `${context.dataset.label}: ${context.parsed.y}`;
        }
      }
    } },
    scales: { 
      y: { beginAtZero: true, max: chartMax },
      percent: { position: 'right', beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } }
    }
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Conversion Report
            <small>Free-to-paid conversion metrics</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Reports</li>
            <li className="active">Conversion</li>
          </ol>
        </section>

        <section className="content">
          <div className="box box-info">
            <div className="box-header with-border">
              <h3 className="box-title">Filter Report</h3>
            </div>
            <div className="box-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Date Range</label>
                    <select className="form-control" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                      <option value="last7days">Last 7 Days</option>
                      <option value="last30days">Last 30 Days</option>
                      <option value="last90days">Last 90 Days</option>
                      <option value="ytd">Year to Date</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                </div>
                {dateRange === 'custom' && (
                  <>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Start Date</label>
                        <input type="date" className="form-control" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>End Date</label>
                        <input type="date" className="form-control" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                      </div>
                    </div>
                  </>
                )}
                <div className="col-md-6" style={{display:'flex', alignItems:'center', justifyContent:'flex-end'}}>
                  <div style={{marginRight:8, alignSelf:'center'}}>Showing: <strong>{rangeLabel(dateRange, customStartDate, customEndDate)}</strong></div>
                  <button className="btn btn-default btn-sm" onClick={() => downloadCSV('conversion_report.csv', (conversionReport?.signupsSeries || []).map(s => ({ date: s.date, signups: s.signups })) )}>Export CSV</button>
                </div>
              </div>
            </div>
          </div>
          <div className="box box-primary">
            <div className="box-header with-border">
              <h3 className="box-title">Conversion Overview</h3>
            </div>
            <div className="box-body">
                  {loading ? <Loader /> : (
                <>
                  <p>Total New Users: {conversionReport?.totalNewUsers || 0}</p>
                  <p>Unique Paid Users: {conversionReport?.uniquePaidUsers || 0}</p>
                  <p>Conversion Rate: {Math.round((conversionReport?.conversionRate || 0) * 100 * 100) / 100}%</p>
                  <div style={{height:420}}>
                    {chartData.datasets[0].data.length === 0 || chartData.datasets.every(ds => ds.data.every(v => v === 0)) ? (
                      <p className="text-center">No conversion data available for the selected range.</p>
                    ) : (
                      <Bar data={chartData} options={{...chartOptions, maintainAspectRatio:false}} />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default ConversionReport;
